import { FormEvent, useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';

type Mode = 'code' | 'password' | 'reset';

function nextTarget() {
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next || !next.startsWith('/') || next.startsWith('//')) return '/';

  try {
    const target = new URL(next, window.location.origin);
    return target.origin === window.location.origin
      ? `${target.pathname}${target.search}${target.hash}`
      : '/';
  } catch {
    return '/';
  }
}

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('code');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    api<{ user: unknown }>('/auth/me')
      .then(() => window.location.replace(nextTarget()))
      .catch(() => undefined);
  }, []);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage('');
    try {
      await action();
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : error instanceof Error ? error.message : '操作失败');
    } finally {
      setBusy(false);
    }
  }, []);

  const sendCode = useCallback(async (target: string) => {
    if (!target) { setMessage('请输入邮箱。'); return false; }
    setBusy(true);
    setMessage('');
    try {
      await api('/auth/request-code', { method: 'POST', body: JSON.stringify({ email: target }) });
      setCountdown(60);
      return true;
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : error instanceof Error ? error.message : '发送失败');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    if (step === 'email') return void sendCode(email).then((ok) => { if (ok) setStep('code'); });
    void run(async () => {
      if (!/^\d{6}$/.test(code)) return setMessage('请输入 6 位数字验证码。');
      await api('/auth/verify', { method: 'POST', body: JSON.stringify({ email, code }) });
      window.location.replace(nextTarget());
    });
  };

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (!email || !password) return setMessage('请输入邮箱和密码。');
      await api('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      window.location.replace(nextTarget());
    });
  };

  const submitReset = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (!/^\d{6}$/.test(resetCode)) return setMessage('请输入 6 位数字验证码。');
      if (resetPassword.length < 8) return setMessage('密码至少 8 位。');
      await api('/auth/password-reset', { method: 'POST', body: JSON.stringify({ email, code: resetCode, password: resetPassword }) });
      setMessage('密码已重置，请用新密码登录。');
      setPassword(resetPassword);
      setMode('password');
    });
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setMessage('');
    setStep('email');
  };

  return <main className="login-view">
    <aside className="login-cover">
      <div className="login-cover-inner">
        <div className="login-brand">菜根</div>
        <div className="login-seal"><b>1902</b><i>CAIGEN</i></div>
        <p>CAIGEN ID · 统一账户</p>
      </div>
      <div className="login-motto">嚼得菜根 · 做得大事</div>
    </aside>

    <section className="login-stage">
      <form className="login-sheet" onSubmit={mode === 'code' ? submitCode : mode === 'password' ? submitPassword : submitReset}>
        <header className="login-head">
          <h1>{mode === 'reset' ? '重置密码' : '登录菜根'}</h1>
          <p>一个账户，通行菜根日历 · Wordess · 蓝鲸U</p>
        </header>

        {mode !== 'reset' && <div className="login-tabs">
          <button type="button" className={mode === 'code' ? 'on' : ''} onClick={() => switchMode('code')}>验证码登录</button>
          <button type="button" className={mode === 'password' ? 'on' : ''} onClick={() => switchMode('password')}>密码登录</button>
        </div>}

        <p className="login-email-hint">推荐使用@nju.edu.cn、@smail.nju.edu.cn、@lamda.nju.edu.cn邮箱注册使用，可以使用私人邮箱</p>

        {mode === 'code' && <>
          <label className="login-field">电子邮箱
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@smail.nju.edu.cn" autoComplete="email" disabled={step === 'code'} />
          </label>
          {step === 'email'
            ? <button type="submit" className="login-primary" disabled={busy}>发送验证码</button>
            : <>
              <label className="login-field">六位验证码（发往 {email}）
                <input className="login-code" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="······" inputMode="numeric" />
              </label>
              <button type="submit" className="login-primary" disabled={busy}>进入菜根</button>
              <button type="button" className="login-link" disabled={countdown > 0 || busy} onClick={() => void sendCode(email)}>{countdown > 0 ? `${countdown}s 后重发` : '重新发送验证码'}</button>
            </>}
        </>}

        {mode === 'password' && <>
          <label className="login-field">电子邮箱
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@smail.nju.edu.cn" autoComplete="email" />
          </label>
          <label className="login-field">密码
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="······" autoComplete="current-password" />
          </label>
          <button type="submit" className="login-primary" disabled={busy}>登录</button>
          <button type="button" className="login-link" onClick={() => switchMode('reset')}>忘记密码？</button>
        </>}

        {mode === 'reset' && <>
          <label className="login-field">电子邮箱
            <span className="login-inline">
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@smail.nju.edu.cn" autoComplete="email" />
              <button type="button" className="login-code-button" disabled={countdown > 0 || busy} onClick={() => void sendCode(email)}>{countdown > 0 ? `${countdown}s` : '发送验证码'}</button>
            </span>
          </label>
          <label className="login-field">六位验证码
            <input className="login-code" value={resetCode} onChange={(event) => setResetCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="······" inputMode="numeric" />
          </label>
          <label className="login-field">新密码（至少 8 位）
            <input type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} placeholder="······" autoComplete="new-password" />
          </label>
          <button type="submit" className="login-primary" disabled={busy}>重置密码</button>
          <button type="button" className="login-link" onClick={() => switchMode('password')}>返回登录</button>
        </>}

        <p className={`login-message${message ? ' show' : ''}`} role="status">{message}</p>
        {mode !== 'reset' && <p className="login-hint">新邮箱先用验证码注册，登录后到账户中心设置密码，下次即可用邮箱+密码登录。</p>}
      </form>
    </section>
  </main>;
}
